import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { BehaviorSubject, Observable, from, map, switchMap } from "rxjs";
import { ResponseDataInterfaces } from "src/app/models/Interfaces/ResponseDataInterfaces";
import {
  FormBuilderSessionType,
  ICalendarSessionItem,
  IPostSessionBack,
  ISessionDetailsResponse,
} from "../../interfaces/tipo-sesion";
import { AuthenticationService } from "src/app/auth/services/authentication.service";
import { calendarUtils } from "../../utils/convertDates.utils";
import { TokenService } from "src/app/services/CornerStoneServices/token/token.service";
import {
  ILocalization,
  ILocalizationsResponse,
} from "../../interfaces/tipo-localizacion";

function safeParsePonentes(raw: unknown): any[] {
  if (!raw) {
    return [];
  }
  try {
    const s = typeof raw === "string" ? raw.trim() : String(raw);
    if (s.startsWith("[") || s.startsWith("{")) {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {}
  return [];
}

@Injectable({ providedIn: "root" })
export class CalendarService {
  private readonly catalogsUri = `${environment.apiUrlCap}/api/calendarSessions`;
  private readonly cornerEndPoint = `${environment.apiUrlCap}/api/cornerStone/request`;

  private sessionsByPerson = new BehaviorSubject<ICalendarSessionItem[]>([]);
  private sessionsByPersonObs = this.sessionsByPerson.asObservable();

  constructor(
    private httpClient: HttpClient,
    private authService: AuthenticationService,
    private tokenService: TokenService
  ) {}
  public getSessionsByPersonObservable(): Observable<ICalendarSessionItem[]> {
    return this.sessionsByPersonObs;
  }

  // // Pipes
  public transformToBackDto(
    d: FormBuilderSessionType,
    userId: number,
    creationDate: Date,
    qrMaestro: boolean
  ): IPostSessionBack {
    const newSession: IPostSessionBack = {
      idSesionCalendario: d.idSesionCalendario ?? undefined,
      nombreSesion: d.nombreSession,
      numeroPersona: this.authService.currentUserValue.personNumber ?? "",
      idPlaneacionCurso: Number(d.idPlaneacionCurso),
      idTipoSesion: parseInt(d.tipoSesion),
      ponentes: d.ponentes,
      ubicacion: d.location.name,
      idLocacion: d.location.externalId,
      nivel: d.nivel ?? "",
      fechaAlta: creationDate,
      idUsuario: userId,
      arrayPonentes: d.arrayPonentes,
      MasterQR: qrMaestro,
      partsCalendarSessions: d.partes.map(part => ({
        fkIdSesionCalendario: part.fkIdSesionCalendario ?? 0,
        idSesionCalendarioParte: part.idSesionCalendarioParte ?? 0,
        fecha: part.fecha,
        fechaInicio: part.fechaInicio,
        fechaFin: part.fechaFin,
      })),
      objectIdSesion: d.objectIdSesion ?? undefined,
    };
    return newSession;
  }

  // Http Request
  public getSessionsById(userId: string) {
    const params = new HttpParams().set("userId", userId);
    const url = `${this.catalogsUri}/partCalendarSession`;
    this.httpClient
      .get<ResponseDataInterfaces<ICalendarSessionItem[]>>(url, { params })
      .pipe(
        map(({ data }): ICalendarSessionItem[] => {
          return data.map(item => ({
            ...item,
            fechaInicio: calendarUtils.convertToLocalDate(item.fechaInicio),
            fechaFin: calendarUtils.convertToLocalDate(item.fechaFin),
            fechaAlta: item.fechaAlta
              ? calendarUtils.convertToLocalDate(item.fechaAlta)
              : undefined,
          }));
        })
      )
      .subscribe(data => {
        this.sessionsByPerson.next(data);
      });
  }

  public getSessionDetails(
    calendarSessionId: string | number
  ): Observable<ISessionDetailsResponse> {
    const url = `${this.catalogsUri}/${calendarSessionId.toString()}`;
    return this.httpClient
      .get<ResponseDataInterfaces<ISessionDetailsResponse>>(url)
      .pipe(
        map(d => {
          d.data.partsCalendarSessions = d.data.partsCalendarSessions.map(
            i => ({
              ...i,
              fechaInicio: calendarUtils.convertToLocalDate(i.fechaInicio),
              fechaFin: calendarUtils.convertToLocalDate(i.fechaFin),
              fechaAlta: calendarUtils.convertToLocalDate(d.data.fechaAlta),
            })
          );
          const rawPonentes = (d.data as any).ponentes;
          (d.data as any).ponentesParsed = safeParsePonentes(rawPonentes);
          return d.data;
        })
      );
  }

  public httpSessionPutPatch(
    payload: IPostSessionBack,
    isNew: boolean,
    qrMaestro: boolean
  ) {
    const uri = this.catalogsUri;
    const headers = { "Content-Type": "application/json" };

    return isNew
      ? this.httpClient.post(uri, payload, { headers })
      : this.httpClient.put(uri, payload, { headers });
  }

  public retrySessionProcess(payload: IPostSessionBack) {
    const uri = `${this.catalogsUri}/retryCalendarSessionProcess`;
    const headers = { "Content-Type": "application/json" };
    return this.httpClient.put(uri, payload, { headers });
  }

  public deleteSession(idSessionCalendar: number) {
    const uri = `${this.catalogsUri}/delete`;
    const headers = { "Content-Type": "application/json" };
    const params = new HttpParams().set("idSessionCalendar", idSessionCalendar);
    return this.httpClient.put(uri, { headers, params });
  }
  public deleteCalendarSessionPart(idSessionCalendar: number) {
    const uri = `${this.catalogsUri}/partCalendarSession/${idSessionCalendar}`;
    return this.httpClient.delete(uri);
  }

  public getLocationFromCornerStone(
    searchTerm: string,
    searchType: "name" | "externalId"
  ): Observable<ILocalization[]> {
    return from(this.tokenService.getToken("all")).pipe(
      switchMap(data => {
        const RBody = {
          path: `/x/organizations/v1/ous?typeId=32&${searchType}=${searchTerm}`,
          token: data.access_token,
          method: "GET",
          body: "",
        };
        return this.httpClient
          .post<ILocalizationsResponse>(this.cornerEndPoint, RBody)
          .pipe(map(d => d.data));
      })
    );
  }
}
